import React, { useEffect, useState } from 'react'
import { MdPauseCircleOutline, MdDeleteOutline } from 'react-icons/md'
import type { ScheduledChange } from '@/utils/weeklyOrder'
import '@/styles/components/daily-delivery-card.css'

const PRODUCT_IMAGE = '/milk-bottle.jpg'
const formatPrice = (price: number): string => `£${price.toFixed(2)}`

interface Props {
  name: string
  image?: string | null
  unitPrice?: number | null
  frequency?: string | null
  /** What the customer gets of this product on this day right now. */
  standingQty: number
  /** Scheduled changes on this product for this day, shown with a Cancel each. */
  changes?: ScheduledChange[]
  /** Undo a scheduled change; handed to the dashboard's confirm dialog. */
  onCancelChange?: (change: ScheduledChange) => void
  disabled?: boolean
  onViewDetail?: () => void
  /** Apply a new quantity for this day; opens the "when" dialog. */
  onChangeQuantity: (newQty: number) => void
  /** Pause this product; opens the "when" dialog. */
  onPause: () => void
  /** Remove this product; opens the "when" dialog. */
  onDelete: () => void
}

/**
 * A product on one delivery day, edited in place.
 *
 * The card is the "what": step the quantity, or pause, or remove. Only the
 * "when" is left to a dialog, launched from here. The quantity stepper stages a
 * target locally (adjust, then Save), so tapping the arrows doesn't fire a
 * dialog on every press. Removing is the Delete button, so the stepper floors
 * at 1 rather than turning a quantity of zero into a hidden removal.
 */
const DailyDeliveryCard: React.FC<Props> = ({
  name,
  image,
  unitPrice,
  frequency,
  standingQty,
  changes = [],
  onCancelChange,
  disabled = false,
  onViewDetail,
  onChangeQuantity,
  onPause,
  onDelete,
}) => {
  const [target, setTarget] = useState(standingQty)

  // Re-seed after a save + refresh (standingQty only changes then, never mid-edit).
  useEffect(() => { setTarget(standingQty) }, [standingQty])

  const changed = target !== standingQty

  return (
    <div className="ddc">
      <div className="ddc-top">
        <button
          type="button"
          className="ddc-thumb"
          onClick={onViewDetail}
          disabled={!onViewDetail}
          aria-label={onViewDetail ? `About ${name}` : undefined}
        >
          <img
            src={image || PRODUCT_IMAGE}
            alt=""
            onError={e => { e.currentTarget.src = PRODUCT_IMAGE }}
          />
        </button>

        {/* Left: name, then Pause / Delete beneath it. */}
        <div className="ddc-body">
          <div className="ddc-head">
            <button type="button" className="ddc-name" onClick={onViewDetail} disabled={!onViewDetail}>
              {name}
            </button>
            {frequency && <span className="ddc-freq">{frequency}</span>}
          </div>

          <div className="ddc-actions">
            <button type="button" className="ddc-action" onClick={onPause} disabled={disabled}>
              <MdPauseCircleOutline aria-hidden="true" /> Pause
            </button>
            <button type="button" className="ddc-action is-delete" onClick={onDelete} disabled={disabled}>
              <MdDeleteOutline aria-hidden="true" /> Stop
            </button>
          </div>
        </div>

        {/* Right: price, the stepper, then Save on its own line, centred. */}
        <div className="ddc-side">
          {unitPrice != null && <span className="ddc-price">{formatPrice(unitPrice)} each</span>}

          <div className="quantity-control ddc-qty">
            <button
              type="button"
              className="qty-btn"
              onClick={() => setTarget(q => Math.max(1, q - 1))}
              disabled={disabled || target <= 1}
              aria-label="One fewer"
            >
              −
            </button>
            <input
              type="number"
              className="qty-input"
              min={1}
              value={target}
              disabled={disabled}
              onChange={e => setTarget(Math.max(1, parseInt(e.target.value) || 1))}
            />
            <button
              type="button"
              className="qty-btn"
              onClick={() => setTarget(q => q + 1)}
              disabled={disabled}
              aria-label="One more"
            >
              +
            </button>
          </div>

          {changed && (
            <button
              type="button"
              className="ddc-save"
              onClick={() => {
                // Hand the new quantity to the date dialog, then clear the
                // staged value: a scheduled change often leaves the standing
                // quantity unchanged (it shows as an annotation instead), so
                // waiting for standingQty to move would leave Save stuck on.
                onChangeQuantity(target)
                setTarget(standingQty)
              }}
              disabled={disabled}
            >
              Save
            </button>
          )}
        </div>
      </div>

      {/* Scheduled changes, full width beneath, each with a Cancel. */}
      {changes.length > 0 && (
        <ul className="ddc-notes">
          {changes.map((change, i) => (
            <li
              key={`${change.kind}-${change.day}-${change.lineId}-${i}`}
              className={`ddc-note is-${change.kind === 'ending' ? 'ending' : 'change'}`}
            >
              <span className="ddc-note-dot" aria-hidden="true" />
              <span className="ddc-note-body">
                {change.text}
                {onCancelChange && (
                  <>
                    {' ('}
                    <button
                      type="button"
                      className="ddc-note-cancel"
                      onClick={() => onCancelChange(change)}
                      disabled={disabled}
                    >
                      Cancel
                    </button>
                    {')'}
                  </>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default DailyDeliveryCard
